/* Store builder kit · video behaviour for the video wall, the product demo and the feature videos.
   Videos carry data-src and preload="none": the file is attached only when a card nears the screen, then plays
   muted + looped while at least 60% visible and pauses when it leaves. A tap turns the sound on (muting every other
   video on the page); the next taps pause / play. Reduced motion: no autoplay, the first tap plays with sound.
   Posters are lazy too (speed pass 2026-10-17: 12 offscreen posters were 1.08 MB at load on a phone): the markup
   carries data-poster, and the poster attribute is set when the video comes within 300 px of the screen, at the
   width the frame needs (its CSS width x DPR capped at 2, rounded up to a step) when the URL is on Shopify's CDN. */
const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const POSTER_STEPS = [360, 540, 720, 900, 1080, 1440];

function posterUrl(video) {
  const u = video.dataset.poster;
  if (!/\/cdn\/shop\/|cdn\.shopify\.com/.test(u)) return u;
  const css = video.getBoundingClientRect().width || video.clientWidth || 360;
  const need = Math.ceil(css * Math.min(window.devicePixelRatio || 1, 2));
  const w = POSTER_STEPS.find((s) => s >= need) || POSTER_STEPS[POSTER_STEPS.length - 1];
  try { const x = new URL(u, window.location.href); x.searchParams.set('width', w); return x.href; } catch (e) { return u; }
}

function showPoster(video) {
  if (video && video.dataset.poster && !video.getAttribute('poster')) video.poster = posterUrl(video);
}

let posterIO = null;
function watchPosters(videos) {
  const list = videos.filter((v) => v && v.dataset.poster);
  if (!list.length) return;
  if (!('IntersectionObserver' in window)) { list.forEach(showPoster); return; }
  posterIO = posterIO || new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { showPoster(e.target); posterIO.unobserve(e.target); } });
  }, { rootMargin: '300px 300px' });
  list.forEach((v) => posterIO.observe(v));
}

function load(video) {
  showPoster(video);
  if (video.dataset.src && !video.getAttribute('src')) {
    video.src = video.dataset.src;
    video.preload = 'auto';
  }
}

function setState(card, video, labels) {
  const btn = card.querySelector('[data-sb-vw-tap]');
  const paused = video.paused;
  const sound = !video.muted;
  card.classList.toggle('sb-paused', paused && (card.dataset.userPaused === '1' || reduce()));
  card.classList.toggle('sb-sound', sound);
  if (btn) btn.setAttribute('aria-label', paused ? labels.play : sound ? labels.pause : labels.on);
}

function initWall(root) {
  if (root.dataset.sbReady) return;
  root.dataset.sbReady = '1';
  const labels = { on: root.dataset.lOn || 'Turn sound on', off: root.dataset.lOff || 'Turn sound off', play: root.dataset.lPlay || 'Play video', pause: root.dataset.lPause || 'Pause video' };
  const cards = Array.from(root.querySelectorAll('.sb-vw__card')).filter((c) => c.querySelector('video'));
  if (!cards.length) return;
  watchPosters(cards.map((c) => c.querySelector('video')));
  const still = reduce();

  cards.forEach((card) => {
    const video = card.querySelector('video');
    if (still) card.classList.add('sb-paused');
    ['play', 'pause', 'volumechange'].forEach((ev) => video.addEventListener(ev, () => setState(card, video, labels)));
    const btn = card.querySelector('[data-sb-vw-tap]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      load(video);
      if (video.paused) {
        card.dataset.userPaused = '0';
        if (still) video.muted = false;
        video.play().catch(() => {});
      } else if (video.muted) {
        document.querySelectorAll('.sb-vw video').forEach((v) => { if (v !== video) v.muted = true; });
        video.muted = false;
      } else {
        card.dataset.userPaused = '1';
        video.pause();
      }
      setState(card, video, labels);
    });
  });

  if (!('IntersectionObserver' in window)) {
    cards.forEach((c) => load(c.querySelector('video')));
    return;
  }
  // 1) attach the file a little before the card is visible
  const near = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { load(e.target.querySelector('video')); near.unobserve(e.target); } });
  }, { rootMargin: '300px 300px' });
  // 2) play while mostly visible, pause when not (never resumes a video the visitor paused)
  const vis = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const video = e.target.querySelector('video');
      if (e.isIntersecting && e.intersectionRatio >= 0.6) {
        if (still || e.target.dataset.userPaused === '1') return;
        load(video);
        video.play().catch(() => { video.muted = true; video.play().catch(() => {}); });
      } else {
        // leaving the screen: pause and drop the sound, so it comes back as a silent autoplay
        if (!video.paused) video.pause();
        video.muted = true;
      }
    });
  }, { threshold: [0, 0.6] });
  cards.forEach((c) => { near.observe(c); vis.observe(c); });
}

// Product demo + feature videos: silent loops, never sound. The file is attached when its frame nears the screen and
// plays while at least half visible. Reduced motion: no autoplay, native controls instead. The demo also lights up
// the step whose start time the video has reached; tapping a step jumps there.
function initAuto(root) {
  if (root.dataset.sbAutoReady) return;
  root.dataset.sbAutoReady = '1';
  const vids = Array.from(root.querySelectorAll('video[data-src]'));
  watchPosters(vids);
  const still = reduce();
  if (still) vids.forEach((v) => { v.controls = true; });
  if (root.hasAttribute('data-sb-demo')) {
    const video = vids[0];
    const steps = Array.from(root.querySelectorAll('[data-at]'));
    const mark = (t) => {
      let on = steps[0];
      steps.forEach((s) => { if (t + 0.05 >= parseFloat(s.dataset.at || '0')) on = s; });
      steps.forEach((s) => s.classList.toggle('is-on', s === on));
    };
    if (video) video.addEventListener('timeupdate', () => mark(video.currentTime));
    steps.forEach((s) => s.addEventListener('click', () => {
      mark(parseFloat(s.dataset.at || '0'));
      if (!video) return;
      load(video);
      video.currentTime = parseFloat(s.dataset.at || '0');
      if (!still) video.play().catch(() => {});
    }));
  }
  if (!vids.length) return;
  if (!('IntersectionObserver' in window)) { vids.forEach(load); return; }
  const near = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { load(e.target); near.unobserve(e.target); } });
  }, { rootMargin: '400px 0px' });
  const vis = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting && e.intersectionRatio >= 0.5) {
        if (still) return;
        load(v);
        v.muted = true;
        v.play().catch(() => {});
      } else if (!v.paused) v.pause();
    });
  }, { threshold: [0, 0.5] });
  vids.forEach((v) => { near.observe(v); vis.observe(v); });
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-sb-vw]').forEach(initWall);
  if (scope !== document && scope.matches && scope.matches('[data-sb-vw]')) initWall(scope);
  scope.querySelectorAll('[data-sb-auto]').forEach(initAuto);
  if (scope !== document && scope.matches && scope.matches('[data-sb-auto]')) initAuto(scope);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
else initAll();
document.addEventListener('shopify:section:load', (e) => initAll(e.target || document));
